import React, { useState, useEffect, useMemo, Fragment } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { Button, Chip, TextField, Select, MenuItem, FormControl, InputLabel } from "@mui/material";
import {
  CheckCircleIcon,
  TruckIcon,
  ArrowPathIcon,
  HomeIcon,
  PlusIcon,
  QrCodeIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { Dialog, Transition } from "@headlessui/react";
import { useDispatch, useSelector } from "react-redux";
import { getStorages } from "../../../slices/inventory/storage";
import { ReportItemConsumptionDialog } from "../service/reports/report-item-consumption-dialog";
import { BulkMarkPickedDialog } from "../service/reports/bulk-mark-picked-dialog";
import { BulkEditConsumptionDialog } from "../service/reports/bulk-edit-consumption-dialog";
import ReportAddItemsDialog from "../service/reports/report-add-items-dialog";
import { ConsumptionSummaryDialog } from "../service/reports/consumption-summary-dialog";
import { fetchProducts } from "../../../api/product";
import toast from "react-hot-toast";
import ModernProductSelectorV2 from "../../utils/Selector/ModernProductSelectorV2";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { PMField } from "../../utils/PMField";
import { PMButton } from "../../utils/PMButton";
import QRCodeScanner from "../../utils/QRCodeScanner";


// Helper functions from order-items.js
const formatPriceInput = (value) => {
  if (value === "" || value === null || value === undefined) return "";
  const stringValue = String(value);
  // Remove all characters except numbers, comma, and dot
  const cleaned = stringValue.replace(/[^0-9,.]/g, "");
  // Replace dot with comma for decimal separator
  return cleaned.replace(/\./g, ",");
};

// Helper function to format price with exactly two decimal places
const formatPriceWithTwoDecimals = (value) => {
  if (value === "" || value === null || value === undefined) return "";
  const stringValue = String(value).trim();
  
  // If empty after trimming, return empty string
  if (stringValue === "") return "";
  
  // If value contains a comma, ensure it has exactly two decimal places
  if (stringValue.includes(",")) {
    const parts = stringValue.split(",");
    const integerPart = parts[0] || "";
    const decimalPart = parts[1] || "";
    
    // Pad decimal part to 2 digits or truncate if more than 2
    let formattedDecimal = decimalPart.substring(0, 2);
    if (formattedDecimal.length === 1) {
      // If only one digit, pad with zero: "21,2" -> "21,20"
      formattedDecimal = formattedDecimal + "0";
    } else if (formattedDecimal.length === 0) {
      // If no digits after comma, add "00": "21," -> "21,00"
      formattedDecimal = "00";
    }
    
    return `${integerPart},${formattedDecimal}`;
  }
  
  // If no comma and value is a valid number, add ",00": "100" -> "100,00"
  if (/^\d+$/.test(stringValue)) {
    return `${stringValue},00`;
  }
  
  // Otherwise return as is
  return stringValue;
};

// Helper function to convert comma-formatted string to number
const parseNumericValue = (value) => {
  if (!value || value === "") return 0;
  const stringValue = String(value);
  // Replace comma with dot for parsing
  const normalized = stringValue.replace(/,/g, ".");
  return parseFloat(normalized) || 0;
};

/** Table display for currency (nb-NO), API values are typically numbers */
const formatMoneyNbNo = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  const num = Number(value);
  if (Number.isNaN(num)) return "—";
  return num.toLocaleString("nb-NO", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatDiscountSurchargeSummary = (item) => {
  const discount = Number(item?.discount) || 0;
  const surcharge = Number(item?.surcharge) || 0;
  const parts = [];
  if (discount) parts.push(`Rabatt ${discount} %`);
  if (surcharge) parts.push(`Påslag ${surcharge} %`);
  return parts.length ? parts.join(" · ") : "—";
};

/** Én kompakt linje til mobil (eks. «129,00 · −10% · +5%»). */
const formatMobilePriceDiscountCompact = (item) => {
  const price = formatMoneyNbNo(item.sales_price);
  const discount = Number(item?.discount) || 0;
  const surcharge = Number(item?.surcharge) || 0;
  const parts = [price];
  if (discount) parts.push(`−${discount}%`);
  if (surcharge) parts.push(`+${surcharge}%`);
  return parts.join(" · ");
};

/** Linjesum eks. mva — samme mønster som `order-items-v3` (rabatt/påslag i % av enhetspris). */
const calculateConsumptionLineTotalExVat = (item) => {
  const salesPrice = parseNumericValue(item.sales_price);
  if (!item || !salesPrice || !item.quantity) return 0;
  const quantity = parseNumericValue(item.quantity);
  const salesPriceDivided = salesPrice / 100;
  const surchargeAmt = item.surcharge
    ? salesPriceDivided * parseNumericValue(item.surcharge)
    : 0;
  const discountAmt = item.discount
    ? salesPriceDivided * parseNumericValue(item.discount)
    : 0;
  return (salesPrice + surchargeAmt - discountAmt) * quantity;
};

const getConsumptionStatusDisplay = (item) => {
  if (item.invoiced) {
    return {
      label: "Fakturert",
      color: "default",
      icon: null,
    };
  }
  const quantityUsed = item.quantity_used || 0;
  if (quantityUsed > 0 && !item.taken_in_vehicle) {
    return {
      label: "Brukt på oppdrag",
      color: "success",
      icon: <CheckCircleIcon className="h-4 w-4" />,
    };
  }
  if (item.taken_in_vehicle) {
    return {
      label: "Tatt med i bilen",
      color: "info",
      icon: <TruckIcon className="h-4 w-4" />,
    };
  }
  if (item.not_used) {
    return {
      label: "Ikke brukt",
      color: "default",
      icon: <CheckCircleIcon className="h-4 w-4" />,
    };
  }
  if (item.returned_to_storage) {
    return {
      label: "Lagt tilbake på lager",
      color: "primary",
      icon: <HomeIcon className="h-4 w-4" />,
    };
  }
  if (item.left_in_vehicle) {
    return {
      label: "Ligger i bilen",
      color: "warning",
      icon: <ArrowPathIcon className="h-4 w-4" />,
    };
  }
  if (item.picked) {
    return {
      label: "Plukket",
      color: "secondary",
      icon: null,
    };
  }
  return {
    label: "Ikke plukket",
    color: "default",
    icon: null,
  };
};

/**
 * Generic ItemConsumptionList component that can be used for both reports and work orders
 * @param {Array} items - Initial items array
 * @param {Function} onItemsChange - Callback when items change
 * @param {boolean} disabled - Whether editing is disabled
 * @param {Object} module - The module object (report or workOrder)
 * @param {string} moduleType - Type of module ('report' or 'work_order')
 * @param {Function} fetchItems - Function to fetch items from API (moduleId) => Promise
 * @param {Function} updateItem - Function to update a single item (moduleId, item) => Promise
 * @param {Function} addItems - Function to add items (moduleId, items) => Promise
 * @param {Function} deleteItem - Function to delete an item (moduleId, itemId) => Promise
 * @param {Function} updateModule - Function to update the module for bulk operations (moduleData) => Promise
 * @param {Function} onModuleUpdate - Callback when module is updated
 * @param {Function} onItemsUpdate - Callback to send items to parent for summary calculations
 * @param {Function} onSubDialogStateChange - Callback to notify parent when sub-dialogs open/close
 */
const ItemConsumptionList = ({
  items: initialItems = [],
  onItemsChange,
  disabled = false,
  module,
  moduleType = "report",
  fetchItems,
  updateItem,
  addItems,
  deleteItem,
  updateModule,
  onModuleUpdate,
  onItemsUpdate, // Callback to send items to parent for summary calculations
  onSubDialogStateChange, // Callback to notify parent when sub-dialogs open/close
}) => {
  const dispatch = useDispatch();
  const storagesState = useSelector((store) => store.inventory_storages);
  const storages = storagesState?.data || [];

  const [items, setItems] = useState([]);
  const [isLoadingItems, setIsLoadingItems] = useState(false);
  const [loadError, setLoadError] = useState(null); // Error state for loading items

  // Helper function to update items and notify parent
  const updateItems = (newItems) => {
    setItems(newItems);
    if (onItemsUpdate) {
      onItemsUpdate(newItems);
    }
  };
  const [addProductDialogOpen, setAddProductDialogOpen] = useState(false);
  const [qrScanMode, setQrScanMode] = useState(false);
  const [manualSearchMode, setManualSearchMode] = useState(false);
  const [manualProductMode, setManualProductMode] = useState(false);
  const [bulkAddDialogOpen, setBulkAddDialogOpen] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const [consumptionDialogOpen, setConsumptionDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [bulkMarkPickedDialogOpen, setBulkMarkPickedDialogOpen] = useState(false);
  const [bulkEditDialogOpen, setBulkEditDialogOpen] = useState(false);
  const [summaryDialogOpen, setSummaryDialogOpen] = useState(false);
  const [summaryData, setSummaryData] = useState(null);
  const [quantityDialogOpen, setQuantityDialogOpen] = useState(false);
  const [productToAdd, setProductToAdd] = useState(null);
  const [productQuantity, setProductQuantity] = useState(1);
  
  // Loading states
  const [isAddingProduct, setIsAddingProduct] = useState(false);
  const [isAddingProducts, setIsAddingProducts] = useState(false);
  const [isSavingConsumption, setIsSavingConsumption] = useState(false);
  const [isDeletingItem, setIsDeletingItem] = useState(false);
  const [isBulkMarkingPicked, setIsBulkMarkingPicked] = useState(false);
  const [isSearchingProduct, setIsSearchingProduct] = useState(false);

  // Track if any sub-dialog is open and notify parent
  useEffect(() => {
    const hasOpenDialog = 
      addProductDialogOpen || 
      bulkAddDialogOpen || 
      consumptionDialogOpen || 
      bulkMarkPickedDialogOpen || 
      bulkEditDialogOpen || 
      summaryDialogOpen ||
      quantityDialogOpen;
    
    if (onSubDialogStateChange) {
      onSubDialogStateChange(hasOpenDialog);
    }
  }, [
    addProductDialogOpen,
    bulkAddDialogOpen,
    consumptionDialogOpen,
    bulkMarkPickedDialogOpen,
    bulkEditDialogOpen,
    summaryDialogOpen,
    quantityDialogOpen,
    onSubDialogStateChange,
  ]);

  // Disable main buttons when any operation is in progress
  const isAnyOperationInProgress = 
    isAddingProduct || 
    isAddingProducts || 
    isSavingConsumption || 
    isDeletingItem || 
    isBulkMarkingPicked ||
    isSearchingProduct;

  const reportConsumptionSumExVat = useMemo(() => {
    if (moduleType !== "report") return 0;
    return items.reduce((sum, item) => sum + calculateConsumptionLineTotalExVat(item), 0);
  }, [moduleType, items]);

  // Load items directly from API when dialog opens
  useEffect(() => {
    if (module?.id && !isLoadingItems && fetchItems) {
      loadItemsFromAPI();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [module?.id, fetchItems]);

  const loadItemsFromAPI = async () => {
    if (!module?.id || !fetchItems) return;
    
    setIsLoadingItems(true);
    setLoadError(null); // Clear previous errors
    try {
      // Use the provided fetchItems function
      const response = await fetchItems(module.id);
      
      // Handle different response structures (report vs work order)
      // Reports: response.data.data, Work orders: response.data
      const itemsData = response?.data?.data || response?.data;
      
      if (itemsData) {
        let moduleItems = Array.isArray(itemsData) ? itemsData : [];
        
        // Filter out quote lines if disabled (not in edit mode) - only for reports
        if (!disabled && moduleType === "report") {
          moduleItems = moduleItems.filter((item) => !item.is_quote_line);
        }
        
        updateItems(moduleItems);
        setLoadError(null); // Clear error on success
        if (onItemsChange) {
          onItemsChange(moduleItems);
        }
      } else {
        // No items found, set empty array
        setItems([]);
        setLoadError(null); // No error, just no items
        if (onItemsChange) {
          onItemsChange([]);
        }
      }
    } catch (error) {
      console.error("Error loading items:", error);
      
      // Show detailed error message
      let errorMessage = "Kunne ikke laste vareforbruk";
      if (error.response?.status === 403) {
        errorMessage = "Du har ikke tilgang til å se vareforbruk. Kontakt administrator.";
      } else if (error.response?.status === 404) {
        errorMessage = moduleType === "report" ? "Rapport ikke funnet." : "Arbeidsordre ikke funnet.";
      } else if (error.response?.data?.errors) {
        errorMessage = error.response.data.errors.join(", ");
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setLoadError(errorMessage); // Store error for display in UI
      toast.error(errorMessage);
      
        // Set empty array on error so UI doesn't show loading state
        updateItems([]);
        if (onItemsChange) {
          onItemsChange([]);
        }
    } finally {
      setIsLoadingItems(false);
    }
  };

  // Save items to backend
  const saveItemsToBackend = async (updatedItems) => {
    if (!module?.id || !updateModule) return;
    
    try {
      const moduleData = {
        id: module.id,
        items: updatedItems.map((item) => ({
          id: item.id,
          product_id: item.product_id, // Can be null for manual items
          quantity: item.quantity,
          unit_id: item.unit_id,
          sales_price: item.sales_price,
          purchase_price: item.purchase_price,
          description: item.description, // Only description for manual items
          discount: item.discount || 0,
          surcharge: item.surcharge || 0,
          picked: item.picked,
          picked_at: item.picked_at,
          taken_in_vehicle: item.taken_in_vehicle,
          returned_to_storage: item.returned_to_storage,
          left_in_vehicle: item.left_in_vehicle,
          storage_id: item.storage_id,
          invoiced: item.invoiced,
        })),
      };

      const result = await updateModule(moduleData);
      
      // Check if we have new items (without id) that need to be loaded from API
      const hasNewItems = updatedItems.some(item => !item.id);
      
      if (hasNewItems) {
        // If we added new items, reload from API to get their IDs
        await loadItemsFromAPI();
      } else {
        // For existing items, update local state with the saved items from backend response
        const savedFromApi = result?.items || result?.data?.items;
        if (savedFromApi) {
          const savedItems = Array.isArray(savedFromApi) ? savedFromApi : [];
          updateItems(savedItems);
          if (onItemsChange) {
            onItemsChange(savedItems);
          }
        } else {
          // Fallback: update local state with what we sent
          updateItems(updatedItems);
          if (onItemsChange) {
            onItemsChange(updatedItems);
          }
        }
      }
      
      return result;
    } catch (error) {
      console.error("Error saving items:", error);
      toast.error("Kunne ikke lagre vareforbruk");
      throw error;
    }
  };

  useEffect(() => {
    if (storages.length === 0) {
      dispatch(getStorages());
    }
  }, [dispatch, storages.length]);

  const handleAddProduct = () => {
    setAddProductDialogOpen(true);
    setQrScanMode(false);
    setManualSearchMode(false);
    setManualProductMode(false);
    setBulkAddDialogOpen(false);
    setSelectedProducts([]);
    setQuantity(1);
  };

  const handleQrCodeScan = async (scannedCode) => {
    if (!scannedCode || scannedCode.trim() === "") {
      toast.error("Skriv inn eller skann en QR-kode");
      return;
    }

    if (isSearchingProduct) return; // Prevent multiple searches

    setIsSearchingProduct(true);
    try {
      // First, try to search by product ID if it's a number
      // (could be from URL like /dashboard/products/1)
      let response = null;
      
      if (/^\d+$/.test(scannedCode.trim())) {
        // It's a numeric ID, try to fetch by ID first
        try {
          response = await fetchProducts({
            ids: scannedCode.trim(),
            page: 1,
            per_page: 1,
            fields: "id,title,displayTitle,item_number,unit,unit_id,sales_price,purchase_price",
          });
        } catch (error) {
          // If ID search fails, fall through to query search
          console.log("Product ID search failed, trying query search");
        }
      }
      
      // If ID search didn't work or code is not numeric, search by query (item_number or title)
      if (!response || !response.data || response.data.length === 0) {
        response = await fetchProducts({
          query: scannedCode.trim(),
          page: 1,
          per_page: 10,
          fields: "id,title,displayTitle,item_number,unit,unit_id,sales_price,purchase_price",
        });
      }
      
      if (response && response.data && response.data.length > 0) {
        const product = response.data[0];
        // For QR code scanning, show quantity dialog immediately
        setProductToAdd(product);
        setProductQuantity(1);
        setQuantityDialogOpen(true);
      } else {
        toast.error("Produkt ikke funnet. Prøv å søke manuelt.");
      }
    } catch (error) {
      console.error("Error searching product:", error);
      toast.error("Kunne ikke søke etter produkt");
    } finally {
      setIsSearchingProduct(false);
    }
  };

  const handleAddProductWithQuantity = async (product, quantity) => {
    if (!product) {
      toast.error("Ingen produkt valgt");
      return;
    }

    if (isAddingProduct) return; // Prevent multiple clicks

    setIsAddingProduct(true);
    try {
      // Ensure we have full product object with all required fields
      let productToAdd = product;
      
      // If product doesn't have full data, fetch it
      if (!product.title && !product.displayTitle) {
        try {
          const response = await fetchProducts({
            ids: product.id.toString(),
            page: 1,
            per_page: 1,
            fields: "id,title,displayTitle,item_number,unit,unit_id,sales_price,purchase_price",
          });
          
          if (response?.data && response.data.length > 0) {
            productToAdd = response.data[0];
          }
        } catch (error) {
          console.error("Error fetching product details:", error);
        }
      }

      // Create new item with specified quantity
      const newItem = {
        id: null, // Will be set by backend
        product_id: productToAdd.id,
        product: productToAdd,
        quantity: parseFloat(quantity) || 1,
        unit_id: productToAdd.unit?.id || productToAdd.unit_id,
        unit: productToAdd.unit,
        sales_price: productToAdd.sales_price,
        purchase_price: productToAdd.purchase_price,
        description: "",
        picked: false,
        taken_in_vehicle: false,
        returned_to_storage: false,
        left_in_vehicle: false,
        storage_id: null,
        invoiced: false,
      };

      // Use addItems API if available, otherwise use saveItemsToBackend
      if (addItems && typeof addItems === 'function' && moduleType === 'work_order') {
        // For work orders, we need to send all items (existing + new) to prevent overwriting
        // Get all current items and add the new one
        const allItems = [...items, newItem];
        const response = await addItems(module.id, allItems);
        
        // Use response directly instead of fetching again
        const responseItems = response?.data?.items || response?.data?.data?.items || allItems;
        if (Array.isArray(responseItems)) {
          updateItems(responseItems);
          if (onItemsChange) {
            onItemsChange(responseItems);
          }
        }
        
        // Reload item_lists to get updated handling list
        if (onModuleUpdate) {
          await onModuleUpdate();
        }
      } else if (addItems && typeof addItems === 'function') {
        // For reports, we need to send all items (existing + new) to prevent overwriting
        // Get all current items and add the new one
        const allItems = [...items, newItem];
        const response = await addItems(module.id, allItems);
        
        // updateReport / Redux unwrap returns the report object at the root; some paths wrap in .data
        const responseItems =
          response?.items ||
          response?.data?.items ||
          response?.data?.data?.items ||
          allItems;
        if (Array.isArray(responseItems)) {
          updateItems(responseItems);
          if (onItemsChange) {
            onItemsChange(responseItems);
          }
        }
      } else {
        // Fallback: use saveItemsToBackend
        const updatedItems = [...items, newItem];
        updateItems(updatedItems);
        await saveItemsToBackend(updatedItems);
        
        // Reload items from API if we have new items without IDs
        const hasNewItems = updatedItems.some(item => !item.id);
        if (hasNewItems) {
          await loadItemsFromAPI();
        }
      }
      
      // Close dialogs and reset state
      setQuantityDialogOpen(false);
      setProductToAdd(null);
      setProductQuantity(1);
      setAddProductDialogOpen(false);
      setQrScanMode(false);
      
      toast.success("Produkt lagt til");
    } catch (error) {
      console.error("Error adding product:", error);
      toast.error("Kunne ikke legge til produkt");
    } finally {
      setIsAddingProduct(false);
    }
  };

  const handleAddProducts = async () => {
    if (selectedProducts.length === 0) {
      toast.error("Velg minst ett produkt");
      return;
    }

    if (isAddingProducts) return; // Prevent multiple clicks

    setIsAddingProducts(true);
    try {
      // Ensure we have full product objects with all required fields
      const productsToAdd = await Promise.all(
        selectedProducts.map(async (product) => {
          // If product already has full data, use it
          if (product.title || product.displayTitle) {
            return product;
          }
          
          // Otherwise, fetch full product data
          try {
            const response = await fetchProducts({
              ids: product.id.toString(),
              page: 1,
              per_page: 1,
              fields: "id,title,displayTitle,item_number,unit,unit_id,sales_price,purchase_price",
            });
            
            if (response?.data && response.data.length > 0) {
              return response.data[0];
            }
            return product; // Fallback to original if fetch fails
          } catch (error) {
            console.error("Error fetching product details:", error);
            return product; // Fallback to original if fetch fails
          }
        })
      );

      // Create all new items first - use quantity from each product
      const newItems = productsToAdd.map((product) => ({
        id: null, // Will be set by backend
        product_id: product.id,
        product: product,
        quantity: product.quantity || quantity || 1, // Use quantity from product object or state
        unit_id: product.unit?.id || product.unit_id,
        unit: product.unit,
        sales_price: product.sales_price,
        purchase_price: product.purchase_price,
        description: "",
        picked: false,
        taken_in_vehicle: false,
        returned_to_storage: false,
        left_in_vehicle: false,
        storage_id: null,
        invoiced: false,
      }));

      // Use addItems API if available, otherwise use saveItemsToBackend
      if (addItems && typeof addItems === 'function' && moduleType === 'work_order') {
        // For work orders, we need to send all items (existing + new) to prevent overwriting
        // Get all current items and add the new ones
        const allItems = [...items, ...newItems];
        const response = await addItems(module.id, allItems);
        
        // Use response directly instead of fetching again
        const responseItems = response?.data?.items || response?.data?.data?.items || allItems;
        if (Array.isArray(responseItems)) {
          updateItems(responseItems);
          if (onItemsChange) {
            onItemsChange(responseItems);
          }
        }
        
        // Reload item_lists to get updated handling list
        if (onModuleUpdate) {
          await onModuleUpdate();
        }
      } else if (addItems && typeof addItems === 'function') {
        // For reports, we need to send all items (existing + new) to prevent overwriting
        // Get all current items and add the new ones
        const allItems = [...items, ...newItems];
        const response = await addItems(module.id, allItems);
        
        const responseItems =
          response?.items ||
          response?.data?.items ||
          response?.data?.data?.items ||
          allItems;
        if (Array.isArray(responseItems)) {
          updateItems(responseItems);
          if (onItemsChange) {
            onItemsChange(responseItems);
          }
        }
      } else {
        // Fallback: use saveItemsToBackend
        const updatedItems = [...items, ...newItems];
        updateItems(updatedItems);
        await saveItemsToBackend(updatedItems);
        
        // Reload items from API if we have new items without IDs
        const hasNewItems = updatedItems.some(item => !item.id);
        if (hasNewItems) {
          await loadItemsFromAPI();
        }
      }
      
      // Close dialog and reset state
      setAddProductDialogOpen(false);
      setSelectedProducts([]);
      setQuantity(1);
      setQrScanMode(false);
      setManualSearchMode(false);
      
      toast.success(`${newItems.length} ${newItems.length === 1 ? "produkt" : "produkter"} lagt til`);
    } catch (error) {
      console.error("Error adding products:", error);
      toast.error("Kunne ikke legge til produkter");
    } finally {
      setIsAddingProducts(false);
    }
  };

  const handleAddItems = async (itemsToAdd) => {
    if (isAddingProducts) return; // Prevent multiple clicks

    setIsAddingProducts(true);
    try {
      // Use addItems API if available, otherwise use saveItemsToBackend
      if (addItems && typeof addItems === 'function' && moduleType === 'work_order') {
        // For work orders, we need to send all items (existing + new) to prevent overwriting
        // Get all current items and add the new ones
        const allItems = [...items, ...itemsToAdd];
        const response = await addItems(module.id, allItems);
        
        // Use response directly instead of fetching again
        const responseItems = response?.data?.items || response?.data?.data?.items || allItems;
        if (Array.isArray(responseItems)) {
          updateItems(responseItems);
          if (onItemsChange) {
            onItemsChange(responseItems);
          }
        }
        
        // Reload item_lists to get updated handling list
        if (onModuleUpdate) {
          await onModuleUpdate();
        }
      } else if (addItems && typeof addItems === 'function') {
        // For reports, we need to send all items (existing + new) to prevent overwriting
        // Get all current items and add the new ones
        const allItems = [...items, ...itemsToAdd];
        const response = await addItems(module.id, allItems);
        
        const responseItems =
          response?.items ||
          response?.data?.items ||
          response?.data?.data?.items ||
          allItems;
        if (Array.isArray(responseItems)) {
          updateItems(responseItems);
          if (onItemsChange) {
            onItemsChange(responseItems);
          }
        }
      } else {
        // Fallback: use saveItemsToBackend
        const updatedItems = [...items, ...itemsToAdd];
        setItems(updatedItems);
        await saveItemsToBackend(updatedItems);
        
        // Reload items from API if we have new items without IDs
        const hasNewItems = updatedItems.some(item => !item.id);
        if (hasNewItems) {
          await loadItemsFromAPI();
        }
      }
      
      toast.success(`${itemsToAdd.length} ${itemsToAdd.length === 1 ? "produkt" : "produkter"} lagt til`);
    } catch (error) {
      console.error("Error adding items:", error);
      toast.error("Kunne ikke legge til produkter");
    } finally {
      setIsAddingProducts(false);
    }
  };

  const openConsumptionDialogForItem = (item) => {
    if (!canEdit(item)) return;
    setSelectedItem(item);
    setConsumptionDialogOpen(true);
  };

  const persistReportLineItem = async (updatedItem) => {
    if (!module?.id || !updateItem) {
      throw new Error("Mangler modul eller updateItem");
    }
    const response = await updateItem(module.id, updatedItem);
    return response?.data?.data || response?.data || updatedItem;
  };

  const handleSaveLineFromConsumption = async (updatedItem) => {
    const savedItem = await persistReportLineItem(updatedItem);
    updateItems(items.map((row) => (row.id === savedItem.id ? savedItem : row)));
    setSelectedItem(savedItem);
    return savedItem;
  };

  const executeDeleteReportItem = async (item) => {
    if (!item?.id || item.picked || item.invoiced) return;
    if (!module?.id || isDeletingItem) return;

    setIsDeletingItem(true);
    try {
      if (deleteItem && typeof deleteItem === "function") {
        await deleteItem(module.id, item.id);
        updateItems(items.filter((row) => row.id !== item.id));
      } else {
        const updatedItems = items.filter((row) => row.id !== item.id);
        await saveItemsToBackend(updatedItems);
        if (onModuleUpdate) await onModuleUpdate();
      }
      toast.success("Produkt slettet");
      setConsumptionDialogOpen(false);
      setSelectedItem(null);
    } catch (error) {
      console.error("Error deleting item:", error);
      toast.error(error.message || "Kunne ikke slette produkt");
    } finally {
      setIsDeletingItem(false);
    }
  };

  const handleBulkMarkPicked = async (itemsToMark, storageId) => {
    if (!module?.id || !updateItem || isBulkMarkingPicked) return; // Prevent multiple clicks
    
    // Validate that items have storage_id (either from item or from parameter)
    const itemsNeedingStorage = itemsToMark.filter((item) => !item.storage_id && !storageId);
    if (itemsNeedingStorage.length > 0) {
      toast.error("Alle produkter må ha et varelager før de kan markeres som plukket");
      return;
    }
    
    setIsBulkMarkingPicked(true);
    try {
      const updatePromises = itemsToMark.map(async (item) => {
        const updatedItem = {
          ...item,
          picked: true,
          picked_at: new Date().toISOString(),
          // Use storage_id from item if it exists, otherwise use the one from dialog
          storage_id: item.storage_id || storageId,
        };
        return await updateItem(module.id, updatedItem);
      });

      const updatedItems = await Promise.all(updatePromises);
      
      // Update local state with the saved items from backend
      // Response structure is: { data: { data: item } }
      const savedItems = updatedItems
        .map(response => response?.data?.data || response?.data)
        .filter(Boolean);
      const updatedItemsList = items.map((item) => {
        const savedItem = savedItems.find(si => si.id === item.id);
        return savedItem || item;
      });
      updateItems(updatedItemsList);
      
      // Reload item_lists to get updated handling list (picking items may affect handling list)
      if (onModuleUpdate) {
        await onModuleUpdate();
      }
      
      toast.success(`${itemsToMark.length} ${itemsToMark.length === 1 ? "produkt" : "produkter"} markert som plukket`);
    } catch (error) {
      console.error("Error marking items as picked:", error);
      const errorMessage = error.response?.data?.errors?.[0] || error.message || "Kunne ikke markere produkter som plukket";
      toast.error(errorMessage);
    } finally {
      setIsBulkMarkingPicked(false);
    }
  };

  const handleBulkSave = async (updatedItem) => {
    if (!module?.id || !updateItem) return;
    
    try {
      await updateItem(module.id, updatedItem);
      // Don't reload here, let the bulk operation handle it
    } catch (error) {
      console.error("Error updating item:", error);
      throw error; // Re-throw to let bulk handler deal with it
    }
  };

  const handleSaveConsumption = async (updatedItem) => {
    if (!module?.id || !updateItem || isSavingConsumption) return; // Prevent multiple clicks
    
    setIsSavingConsumption(true);
    try {
      // Validate that item has an ID
      if (!updatedItem.id) {
        toast.error("Kunne ikke oppdatere: Item mangler ID");
        console.error("Item missing ID:", updatedItem);
        setIsSavingConsumption(false);
        return;
      }
      
      // Update only this single item on backend
      const response = await updateItem(module.id, updatedItem);
      
      // The response structure is: { data: { data: item } }
      const savedItem = response?.data?.data || response?.data || updatedItem;
      
      // Update item in local list with the saved item from backend
      const updatedItems = items.map((item) =>
        item.id === savedItem.id ? savedItem : item
      );
      updateItems(updatedItems);
      
      // Check if handling list might have changed (quantity_not_used, taken_in_vehicle, not_used)
      const oldItem = items.find(item => item.id === updatedItem.id);
      const needsHandlingListUpdate = 
        (oldItem?.quantity_not_used !== savedItem.quantity_not_used) ||
        (oldItem?.taken_in_vehicle !== savedItem.taken_in_vehicle) ||
        (oldItem?.not_used !== savedItem.not_used);
      
      // If handling list might have changed, notify parent to reload only item_lists
      // This is more efficient than reloading the entire module
      if (needsHandlingListUpdate && onModuleUpdate) {
        // onModuleUpdate should now be a lightweight function that only reloads item_lists
        await onModuleUpdate();
      }
      
      setConsumptionDialogOpen(false);
      setSelectedItem(null);
      
      // Show summary if item was updated with consumption status
      const quantityUsed = savedItem.quantity_used || 0;
      if (savedItem.picked && ((quantityUsed > 0 && !savedItem.taken_in_vehicle) || savedItem.taken_in_vehicle)) {
        const summary = calculateSummary([savedItem]);
        setSummaryData(summary);
        setSummaryDialogOpen(true);
      } else {
        toast.success("Vareforbruk oppdatert");
      }
    } catch (error) {
      console.error("Error updating item:", error);
      toast.error("Kunne ikke oppdatere vareforbruk");
    } finally {
      setIsSavingConsumption(false);
    }
  };

  const calculateSummary = (itemsToSummarize) => {
    const used = [];
    const needsHandling = [];

    itemsToSummarize.forEach((item) => {
      const productName = item.product?.displayTitle || item.product?.title || item.description || `Produkt ${item.product_id || "Ukjent"}`;
      const unit = item.unit?.title || "";
      
      const quantityUsed = item.quantity_used || 0;
      if (quantityUsed > 0 && !item.taken_in_vehicle) {
        used.push({
          product: productName,
          quantity: item.quantity_used,
          unit: unit,
        });
      }
      
      if (item.quantity_not_used && item.quantity_not_used > 0) {
        needsHandling.push({
          product: productName,
          quantity: item.quantity_not_used,
          unit: unit,
          reason: item.quantity_used === 0 ? "Ikke brukt (0 registrert)" : "Ikke fullt ut brukt",
        });
      }
    });

    return { used, needsHandling };
  };


  const canDelete = (item) => {
    return !item.picked && !item.invoiced;
  };

  const canEdit = (item) => {
    return !disabled && !item.invoiced;
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-2">
        <h3 className="text-lg font-medium text-gray-900">Vareforbruk</h3>
        {!disabled && (
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            {items.length > 0 && (
              <>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<CheckCircleIcon className="h-5 w-5" />}
                  onClick={() => setBulkMarkPickedDialogOpen(true)}
                  disabled={isLoadingItems || isBulkMarkingPicked}
                  className="w-full sm:w-auto"
                  size="small"
                >
                  {isBulkMarkingPicked ? (
                    "Markerer..."
                  ) : (
                    <>
                      <span className="hidden sm:inline">Marker alle som plukket</span>
                      <span className="sm:hidden">Marker alle plukket</span>
                    </>
                  )}
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<CheckCircleIcon className="h-5 w-5" />}
                  onClick={() => setBulkEditDialogOpen(true)}
                  disabled={isLoadingItems}
                  className="w-full sm:w-auto"
                  size="small"
                >
                  Bulk-rediger
                </Button>
              </>
            )}
            <Button
              variant="contained"
              color="primary"
              startIcon={<PlusIcon className="h-5 w-5" />}
              onClick={handleAddProduct}
              disabled={isLoadingItems || isAnyOperationInProgress}
              className="w-full sm:w-auto"
              size="small"
            >
              Legg til produkt
            </Button>
          </div>
        )}
      </div>

      {isLoadingItems ? (
        <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
          <p className="text-gray-500">Laster vareforbruk...</p>
        </div>
      ) : loadError ? (
        <div className="text-center py-8 bg-red-50 rounded-lg border-2 border-dashed border-red-300">
          <div className="text-red-600 font-medium mb-2">Feil ved lasting av vareforbruk</div>
          <p className="text-red-500 text-sm mb-4">{loadError}</p>
          <Button
            variant="outlined"
            color="error"
            onClick={loadItemsFromAPI}
            className="mt-2"
          >
            Prøv igjen
          </Button>
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <p className="text-gray-500">Ingen produkter lagt til</p>
          {!disabled && (
            <Button
              variant="outlined"
              color="primary"
              onClick={handleAddProduct}
              className="mt-4"
            >
              Legg til første produkt
            </Button>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto -mx-4 sm:mx-0">
          <Table sx={{ width: "100%", minWidth: { xs: "100%", sm: 650 } }}>
            <TableHead>
              <TableRow>
                <TableCell className="font-medium text-gray-900">
                  <span className="sm:hidden">Vareforbruk</span>
                  <span className="hidden sm:inline">Produkt</span>
                </TableCell>
                <TableCell align="center" className="hidden sm:table-cell">Antall/Enhet</TableCell>
                {moduleType === "report" && (
                  <>
                    <TableCell align="right" className="hidden sm:table-cell">
                      Salgspris
                    </TableCell>
                    <TableCell align="center" className="hidden sm:table-cell">
                      Rabatt / påslag
                    </TableCell>
                  </>
                )}
                <TableCell align="center" className="hidden md:table-cell">Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
            {items.map((item) => {
              const statusDisplay = getConsumptionStatusDisplay(item);
              const canEditItem = canEdit(item);

              return (
                <TableRow 
                  key={item.id || item.temp_id} 
                  hover
                  onClick={() => openConsumptionDialogForItem(item)}
                  className={canEditItem ? "cursor-pointer" : ""}
                  sx={{
                    "&:hover": canEditItem ? { backgroundColor: "rgba(0, 0, 0, 0.04)" } : {},
                  }}
                >
                  <TableCell>
                    <div className="min-w-0">
                      <div className="font-medium text-gray-900 whitespace-pre-wrap">
                        {item.product?.displayTitle ||
                          item.product?.title ||
                          item.description ||
                          `Produkt ${item.product_id || "Ukjent"}`}
                      </div>
                      {item.product?.item_number && (
                        <div className="text-sm text-gray-500">
                          Art.nr: {item.product.item_number}
                        </div>
                      )}
                      {!item.product_id && (
                        <div className="text-xs text-gray-500 italic mt-1">
                          Manuell vare
                        </div>
                      )}
                      {item.description && item.product_id && (
                        <div className="text-sm text-gray-600 mt-1 whitespace-pre-wrap">
                          {item.description}
                        </div>
                      )}
                      {/* Mobil: kompakt antall, pris/rabatt, én status-chip */}
                      <div className="sm:hidden mt-2 space-y-1">
                        {moduleType === "report" && (
                          <div className="text-sm text-gray-700 tabular-nums">
                            {formatMobilePriceDiscountCompact(item)}
                          </div>
                        )}
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                          <span className="text-sm font-medium text-gray-800 tabular-nums">
                            {item.quantity || 0}
                            {(item.quantity_used > 0 || item.taken_in_vehicle) && (
                              <span className="text-gray-500 font-normal">
                                {" "}({item.quantity_used > 0 && !item.taken_in_vehicle 
                                  ? `${item.quantity_used} brukt` 
                                  : item.taken_in_vehicle 
                                  ? `${item.quantity_used || item.quantity || 0} tatt med` 
                                  : ""})
                              </span>
                            )}
                            {" "}{item.unit?.title || ""}
                          </span>
                          <Chip
                            label={statusDisplay.label}
                            color={statusDisplay.color}
                            size="small"
                            icon={statusDisplay.icon}
                            sx={{
                              "& .MuiChip-icon": {
                                color: "inherit",
                              },
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell align="center" className="hidden sm:table-cell">
                    <div className="flex items-center justify-center gap-1">
                      <span className="font-medium">
                        {item.quantity || 0}
                        {(item.quantity_used > 0 || item.taken_in_vehicle) && (
                          <span className="text-gray-500 font-normal">
                            {" "}({item.quantity_used > 0 && !item.taken_in_vehicle 
                              ? `${item.quantity_used} brukt` 
                              : item.taken_in_vehicle 
                              ? `${item.quantity_used || item.quantity || 0} tatt med` 
                              : ""})
                          </span>
                        )}
                      </span>
                      <span className="text-sm text-gray-600">
                        {item.unit?.title || ""}
                      </span>
                    </div>
                  </TableCell>
                  {moduleType === "report" && (
                    <>
                      <TableCell align="right" className="hidden sm:table-cell">
                        <span className="font-medium tabular-nums">
                          {formatMoneyNbNo(item.sales_price)}
                        </span>
                      </TableCell>
                      <TableCell align="center" className="hidden sm:table-cell">
                        <span className="text-sm text-gray-800">
                          {formatDiscountSurchargeSummary(item)}
                        </span>
                      </TableCell>
                    </>
                  )}
                  <TableCell align="center" className="hidden md:table-cell">
                    <Chip
                      label={statusDisplay.label}
                      color={statusDisplay.color}
                      size="small"
                      icon={statusDisplay.icon}
                      sx={{
                        "& .MuiChip-icon": {
                          color: "inherit",
                        },
                      }}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {moduleType === "report" && items.length > 0 && (
          <div className="flex justify-end border-t border-gray-200 pt-3 mt-0 px-2 sm:px-0">
            <div className="text-right">
              <span className="text-sm font-semibold text-gray-900">Sum eks. mva: </span>
              <span className="text-sm font-semibold tabular-nums text-gray-900">
                {formatMoneyNbNo(reportConsumptionSumExVat)}
              </span>
              <span className="text-sm text-gray-500 ml-2">({items.length} linjer)</span>
            </div>
          </div>
        )}
        </div>
      )}

      {/* Add Product Selection Dialog */}
      <Transition.Root show={addProductDialogOpen} as={Fragment}>
        <Dialog as="div" className="relative z-[2100]" static onClose={() => {
          setAddProductDialogOpen(false);
          setQrScanMode(false);
          setManualSearchMode(false);
          setManualProductMode(false);
          setSelectedProducts([]);
          setQuantity(1);
        }}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className={`flex min-h-full items-center justify-center text-center ${
              manualSearchMode || manualProductMode 
                ? 'p-0 sm:p-4' 
                : 'p-4'
            }`}>
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className={`relative transform overflow-hidden bg-white text-left shadow-xl transition-all w-full max-w-md md:max-w-6xl lg:max-w-7xl ${
                  manualSearchMode || manualProductMode
                    ? 'h-screen max-h-screen rounded-none sm:rounded-md sm:h-auto sm:max-h-[85vh] flex flex-col min-h-0'
                    : 'rounded-md max-h-[85vh]'
                } ${manualSearchMode || manualProductMode ? 'p-0 sm:px-4 sm:py-4' : 'px-4 py-4'}`}>
                  <div className={`flex items-center justify-between flex-shrink-0 ${manualSearchMode || manualProductMode ? 'px-4 pt-4 pb-2 sm:px-0 sm:pt-0 sm:pb-0' : 'mb-4'}`}>
                    <Dialog.Title className="text-lg font-medium text-gray-900">
                      Legg til produkt
                    </Dialog.Title>
                      <button
                        type="button"
                        onClick={() => {
                          setAddProductDialogOpen(false);
                          setQrScanMode(false);
                          setManualSearchMode(false);
                          setManualProductMode(false);
                          setSelectedProducts([]);
                          setQuantity(1);
                        }}
                        className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
                        aria-label="Lukk"
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                  </div>

                  {!qrScanMode && !manualSearchMode && !manualProductMode && (
                    <div className="space-y-3">
                      <button
                        type="button"
                        onClick={() => setQrScanMode(true)}
                        className="w-full flex items-center justify-start px-4 py-3 text-left border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors"
                      >
                        <QrCodeIcon className="h-5 w-5 mr-3 text-gray-600" />
                        <span className="text-gray-700 font-medium">Skann QR-kode</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setManualSearchMode(true)}
                        className="w-full flex items-center justify-start px-4 py-3 text-left border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors"
                      >
                        <MagnifyingGlassIcon className="h-5 w-5 mr-3 text-gray-600" />
                        <span className="text-gray-700 font-medium">Søk manuelt</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setManualProductMode(true)}
                        className="w-full flex items-center justify-start px-4 py-3 text-left border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors"
                      >
                        <PlusIcon className="h-5 w-5 mr-3 text-gray-600" />
                        <span className="text-gray-700 font-medium">Legg til manuelt produkt</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          // Open bulk dialog first, then close main dialog
                          setBulkAddDialogOpen(true);
                          // Use setTimeout to ensure bulk dialog opens before main dialog closes
                          setTimeout(() => {
                            setAddProductDialogOpen(false);
                          }, 100);
                        }}
                        className="w-full flex items-center justify-start px-4 py-3 text-left border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors"
                      >
                        <PlusIcon className="h-5 w-5 mr-3 text-gray-600" />
                        <span className="text-gray-700 font-medium">Legg til produkter i bulk</span>
                      </button>
                    </div>
                  )}

                  {/* QR Code Scan Mode */}
                  {qrScanMode && (
                    <div className="space-y-4">
                      <QRCodeScanner
                        open={qrScanMode}
                        onScan={handleQrCodeScan}
                        onBack={() => {
                          setQrScanMode(false);
                        }}
                        placeholder="Skriv produktnummer eller skann QR-kode"
                      />
                    </div>
                  )}

                  {/* Manual Search Mode */}
                  {manualSearchMode && (
                    <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
                      <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
                        <ModernProductSelectorV2
                          open={true}
                          embeddedInParentDialog={true}
                          selectedProducts={selectedProducts}
                          onChange={(products) => {
                            setSelectedProducts(products);
                          }}
                          onClose={() => {
                            setManualSearchMode(false);
                          }}
                          multiple={true}
                          filters={[]}
                          showHeader={false}
                          showWarehouseOnlyDefault={true}
                        />
                      </div>
                      {selectedProducts.length > 0 && (
                        <div className="flex-shrink-0 mt-2 px-4 pb-4 sm:px-0 sm:pb-0 flex space-x-2">
                          <Button
                            variant="contained"
                            onClick={() => {
                              handleAddProducts();
                              setManualSearchMode(false);
                            }}
                            className="flex-1"
                            disabled={isAddingProducts}
                          >
                            {isAddingProducts ? "Legger til..." : `Legg til (${selectedProducts.length})`}
                          </Button>
                          <Button
                            variant="outlined"
                            onClick={() => {
                              setSelectedProducts([]);
                            }}
                          >
                            Nullstill
                          </Button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Manual Product Mode */}
                  {manualProductMode && (
                    <div className="flex-1 flex flex-col min-h-0 overflow-hidden sm:block">
                      <div className="flex-1 overflow-y-auto px-4 sm:px-0 sm:space-y-4 space-y-4 min-h-0">
                        <Formik
                        initialValues={{
                          description: "",
                          quantity: 1,
                          sales_price: "",
                          purchase_price: "",
                          discount: "",
                          surcharge: "",
                        }}
                        validationSchema={Yup.object({
                          quantity: Yup.number().min(0.01, "Antall må være større enn 0").required("Antall er påkrevd"),
                        })}
                        onSubmit={async (values, { setSubmitting }) => {
                          try {
                            const newItem = {
                              id: null,
                              product_id: null,
                              product: null,
                              description: values.description || "",
                              quantity: Number(values.quantity) || 1,
                              quantity_picked: Number(values.quantity_picked) || 1,
                              unit_id: null,
                              sales_price: Number(values.sales_price) || 0,
                              purchase_price: Number(values.purchase_price) || 0,
                              discount: Number(values.discount) || 0,
                              surcharge: Number(values.surcharge) || 0,
                              picked: false,
                              taken_in_vehicle: false,
                              returned_to_storage: false,
                              left_in_vehicle: false,
                              storage_id: null,
                              invoiced: false,
                            };

                            await handleAddItems([newItem]);
                            setManualProductMode(false);
                          } catch (error) {
                            console.error("Error adding manual product:", error);
                          } finally {
                            setSubmitting(false);
                          }
                        }}
                      >
                        {(formik) => (
                          <Form>
                            <div className="space-y-4">
                              <PMField
                                name="description"
                                label="Beskrivelse"
                                type="text"
                                {...formik}
                              />
                              <PMField
                                name="quantity"
                                label="Antall"
                                type="number"
                                step="0.01"
                                min="0.01"
                                {...formik}
                              />
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Innkjøpspris
                                  </label>
                                  <TextField
                                    fullWidth
                                    variant="standard"
                                    value={
                                      formik.values.purchase_price !== undefined && formik.values.purchase_price !== null && formik.values.purchase_price !== 0
                                        ? formatPriceWithTwoDecimals(String(formik.values.purchase_price).replace(/\./g, ","))
                                        : ""
                                    }
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      // Allow empty string for easy deletion
                                      if (value === "" || value === null) {
                                        formik.setFieldValue("purchase_price", "");
                                        return;
                                      }
                                      // Parse the input value to a number and store it
                                      const parsed = parseNumericValue(value);
                                      formik.setFieldValue("purchase_price", parsed);
                                    }}
                                    onBlur={(e) => {
                                      formik.handleBlur(e);
                                      // Only format if there's a value, otherwise keep empty
                                      const value = e.target.value;
                                      if (value === "" || value === null) {
                                        formik.setFieldValue("purchase_price", 0);
                                      } else {
                                        const parsed = parseNumericValue(value);
                                        formik.setFieldValue("purchase_price", parsed || 0);
                                      }
                                    }}
                                    onKeyDown={(e) => {
                                      const allowedKeys = /[0-9,.]/;
                                      const isNavigationKey = 
                                        e.key === "Backspace" || 
                                        e.key === "Delete" || 
                                        e.key === "ArrowLeft" || 
                                        e.key === "ArrowRight" || 
                                        e.key === "Tab" ||
                                        e.key === "Enter" ||
                                        (e.key === "a" && e.ctrlKey) || // Ctrl+A for select all
                                        (e.key === "A" && e.ctrlKey);
                                      
                                      if (!allowedKeys.test(e.key) && !isNavigationKey) {
                                        e.preventDefault();
                                      }
                                      if (e.key === ".") {
                                        e.preventDefault();
                                        const syntheticValue = (e.target.value || "") + ",";
                                        formik.setFieldValue("purchase_price", syntheticValue);
                                      }
                                    }}
                                    onFocus={(e) => {
                                      // Select all text when focused for easy replacement
                                      e.target.select();
                                    }}
                                    error={
                                      formik.touched.purchase_price &&
                                      Boolean(formik.errors.purchase_price)
                                    }
                                    helperText={
                                      formik.touched.purchase_price &&
                                      formik.errors.purchase_price
                                    }
                                    placeholder="0,00"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Salgspris
                                  </label>
                                  <TextField
                                    fullWidth
                                    variant="standard"
                                    value={
                                      formik.values.sales_price !== undefined && formik.values.sales_price !== null && formik.values.sales_price !== 0
                                        ? formatPriceWithTwoDecimals(String(formik.values.sales_price).replace(/\./g, ","))
                                        : ""
                                    }
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      // Allow empty string for easy deletion
                                      if (value === "" || value === null) {
                                        formik.setFieldValue("sales_price", "");
                                        return;
                                      }
                                      // Parse the input value to a number and store it
                                      const parsed = parseNumericValue(value);
                                      formik.setFieldValue("sales_price", parsed);
                                    }}
                                    onBlur={(e) => {
                                      formik.handleBlur(e);
                                      // Only format if there's a value, otherwise keep empty
                                      const value = e.target.value;
                                      if (value === "" || value === null) {
                                        formik.setFieldValue("sales_price", 0);
                                      } else {
                                        const parsed = parseNumericValue(value);
                                        formik.setFieldValue("sales_price", parsed || 0);
                                      }
                                    }}
                                    onKeyDown={(e) => {
                                      const allowedKeys = /[0-9,.]/;
                                      const isNavigationKey = 
                                        e.key === "Backspace" || 
                                        e.key === "Delete" || 
                                        e.key === "ArrowLeft" || 
                                        e.key === "ArrowRight" || 
                                        e.key === "Tab" ||
                                        e.key === "Enter" ||
                                        (e.key === "a" && e.ctrlKey) || // Ctrl+A for select all
                                        (e.key === "A" && e.ctrlKey);
                                      
                                      if (!allowedKeys.test(e.key) && !isNavigationKey) {
                                        e.preventDefault();
                                      }
                                      if (e.key === ".") {
                                        e.preventDefault();
                                        const syntheticValue = (e.target.value || "") + ",";
                                        formik.setFieldValue("sales_price", syntheticValue);
                                      }
                                    }}
                                    onFocus={(e) => {
                                      // Select all text when focused for easy replacement
                                      e.target.select();
                                    }}
                                    error={
                                      formik.touched.sales_price &&
                                      Boolean(formik.errors.sales_price)
                                    }
                                    helperText={
                                      formik.touched.sales_price &&
                                      formik.errors.sales_price
                                    }
                                    placeholder="0,00"
                                  />
                                </div>
                              </div>
                              <div className="grid grid-cols-2 gap-4">
                                <PMField
                                  name="discount"
                                  label="Rabatt (%)"
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  {...formik}
                                />
                                <PMField
                                  name="surcharge"
                                  label="Påslag (%)"
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  {...formik}
                                />
                              </div>
                              <div className="flex justify-between items-center pt-4 pb-4 sm:pt-0 sm:pb-0">
                                <PMButton
                                  type="button"
                                  variant="outlined"
                                  size="small"
                                  onClick={() => {
                                    formik.resetForm();
                                    setManualProductMode(false);
                                  }}
                                  className="sm:block hidden"
                                >
                                  Avbryt
                                </PMButton>
                                <PMButton
                                  type="submit"
                                  variant="contained"
                                  disabled={formik.isSubmitting}
                                  size="large"
                                  className="flex-1 sm:flex-initial"
                                >
                                  Legg til
                                </PMButton>
                              </div>
                            </div>
                          </Form>
                        )}
                      </Formik>
                      </div>
                    </div>
                  )}

                  {!qrScanMode && !manualSearchMode && !manualProductMode && (
                    <div className="mt-4 flex justify-end">
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setAddProductDialogOpen(false);
                          setQrScanMode(false);
                          setManualSearchMode(false);
                          setManualProductMode(false);
                          setSelectedProducts([]);
                          setQuantity(1);
                        }}
                      >
                        Lukk
                      </Button>
                    </div>
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Bulk Add Items Dialog */}
      <ReportAddItemsDialog
        open={bulkAddDialogOpen}
        onClose={() => {
          setBulkAddDialogOpen(false);
        }}
        onSave={(itemsToAdd) => {
          handleAddItems(itemsToAdd);
          setBulkAddDialogOpen(false);
        }}
        report={module}
        onSubDialogStateChange={(hasOpen) => {
          // Notify parent about sub-dialog state
          if (onSubDialogStateChange) {
            onSubDialogStateChange(hasOpen || bulkAddDialogOpen);
          }
        }}
      />

      {/* Consumption Dialog */}
      {consumptionDialogOpen && selectedItem && (
        <ReportItemConsumptionDialog
          open={consumptionDialogOpen}
          onClose={() => {
            setConsumptionDialogOpen(false);
            setSelectedItem(null);
          }}
          item={selectedItem}
          report={module}
          onSave={handleSaveConsumption}
          lineEditDisabled={disabled}
          canDeleteLine={Boolean(selectedItem && !disabled && canDelete(selectedItem))}
          onSaveLine={disabled ? undefined : handleSaveLineFromConsumption}
          onDeleteLine={disabled ? undefined : executeDeleteReportItem}
          deleteInProgress={isDeletingItem}
        />
      )}

      {/* Bulk Mark Picked Dialog */}
      <BulkMarkPickedDialog
        open={bulkMarkPickedDialogOpen}
        onClose={() => setBulkMarkPickedDialogOpen(false)}
        items={items}
        onConfirm={handleBulkMarkPicked}
      />

      {/* Bulk Edit Dialog */}
      <BulkEditConsumptionDialog
        open={bulkEditDialogOpen}
        onClose={(summary) => {
          setBulkEditDialogOpen(false);
          loadItemsFromAPI();
          if (summary) {
            setSummaryData(summary);
            setSummaryDialogOpen(true);
          }
        }}
        items={items}
        report={module}
        onSave={handleBulkSave}
      />


      {/* Summary Dialog */}
      <ConsumptionSummaryDialog
        open={summaryDialogOpen}
        onClose={() => {
          setSummaryDialogOpen(false);
          setSummaryData(null);
        }}
        summaryData={summaryData}
      />

      {/* Quantity Dialog for QR Code Scanned Products */}
      <Transition.Root show={quantityDialogOpen} as={Fragment}>
        <Dialog as="div" className="relative z-[2100]" onClose={() => {
          setQuantityDialogOpen(false);
          setProductToAdd(null);
          setProductQuantity(1);
        }}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all w-full max-w-md sm:my-8 sm:p-6">
                  <Dialog.Title className="text-lg font-medium text-gray-900 mb-4">
                    Legg til produkt
                  </Dialog.Title>

                  {productToAdd && (
                    <div className="space-y-4">
                      <div>
                        <div className="text-sm font-medium text-gray-700 mb-1">
                          Produkt:
                        </div>
                        <div className="p-3 bg-blue-50 rounded-lg">
                          <div className="font-medium text-gray-900">
                            {productToAdd.displayTitle || productToAdd.title || `Produkt ${productToAdd.id}`}
                          </div>
                          {productToAdd.item_number && (
                            <div className="text-sm text-gray-500 mt-1">
                              Art.nr: {productToAdd.item_number}
                            </div>
                          )}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Antall *
                        </label>
                        <TextField
                          fullWidth
                          type="number"
                          variant="outlined"
                          value={productQuantity}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === "" || (!isNaN(value) && parseFloat(value) >= 0)) {
                              setProductQuantity(value);
                            }
                          }}
                          inputProps={{
                            step: "0.01",
                            min: "0.01",
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "0.5rem",
                            },
                          }}
                          autoFocus
                          onKeyPress={(e) => {
                            if (e.key === "Enter") {
                              if (productQuantity && parseFloat(productQuantity) > 0) {
                                handleAddProductWithQuantity(productToAdd, productQuantity);
                              }
                            }
                          }}
                        />
                        {productToAdd.unit?.title && (
                          <div className="text-sm text-gray-500 mt-1">
                            Enhet: {productToAdd.unit.title}
                          </div>
                        )}
                      </div>

                      <div className="flex justify-end space-x-2 pt-4">
                        <Button
                          variant="outlined"
                          onClick={() => {
                            setQuantityDialogOpen(false);
                            setProductToAdd(null);
                            setProductQuantity(1);
                          }}
                        >
                          Avbryt
                        </Button>
                        <Button
                          variant="contained"
                          onClick={() => {
                            if (productQuantity && parseFloat(productQuantity) > 0) {
                              handleAddProductWithQuantity(productToAdd, productQuantity);
                            } else {
                              toast.error("Antall må være større enn 0");
                            }
                          }}
                          disabled={!productQuantity || parseFloat(productQuantity) <= 0 || isAddingProduct}
                        >
                          {isAddingProduct ? "Legger til..." : "Legg til"}
                        </Button>
                      </div>
                    </div>
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Delete confirmation er integrert i Rediger vareforbruk */}
    </div>
  );
};

export default ItemConsumptionList;

